import createMessageBusClient from "../../lib/messageBusClient";
import addAssemblerItem from "../assembler";
import { PARTICLE, SLIDESHOW } from "../assembler/consts";
import addAssemblerParticle from "../assembler/particleItem";
import addAssemblerSlideshow from "../assembler/slideshowParticleItem";
import { insertParticle } from "../assembler/common";
import injectAssemblerAds from "../assembler/ads";
import particleNotify from "../assembler/notification";
import {
    renderAssemblerParticleList,
    updateAssemblerParticleList,
} from "../assembler/particleList";
import curry from "../../lib/core/functional/curry";
import PaginationManager from "./paginationManager";

const makeParticleRenderer = curry(function makeParticleRenderer(container, settings, data) {
    const particle = addAssemblerItem(container, data, settings);
    updateAssemblerParticleList(particle, data);
});

function numericKeysReducer(elements, [key, value]) {
    if (!isNaN(key)) {
        return [value, ...elements];
    }
    return elements;
}

function readPaginationSettings(listicleBody) {
    // GET param overrides for testing: ?liveblog_use_pagination=true&liveblog_pagination_order=5
    const urlParams = new URLSearchParams(window.location.search);
    const paramPaginated = urlParams.get("liveblog_use_pagination");
    const paramOrder = urlParams.get("liveblog_pagination_order");

    const isPaginated =
        paramPaginated === "true" || listicleBody.dataset.listiclePaginated === "true";

    if (!isPaginated) {
        return { paginated: false, pageSize: 0 };
    }

    let paginationOrder = [];
    try {
        // GET param accepts a plain number (e.g. ?liveblog_pagination_order=5)
        // or a JSON array (e.g. ?liveblog_pagination_order=[3,6,9])
        const raw = paramOrder || listicleBody.dataset.listiclePaginationOrder || "[]";
        paginationOrder = JSON.parse(raw.includes("[") ? raw : `[${raw}]`);
    } catch (e) {
        // invalid value – treat as not paginated
    }

    // pagination_order: [N] = N items per page; multiple values use first as page size
    const pageSize = paginationOrder.length > 0 ? paginationOrder[0] : 0;
    if (!pageSize) {
        return { paginated: false, pageSize: 0 };
    }

    return { paginated: true, pageSize };
}

/**
 * Returns the height of whatever fixed/sticky chrome is currently pinned at
 * the very top of the viewport, by sampling elements at that coordinate.
 * Avoids querying the entire DOM.
 */
function getFixedHeaderHeight() {
    // Sample the top-centre of the viewport — any topbar will be hit here
    const elements = document.elementsFromPoint(window.innerWidth / 2, 1);
    return elements.reduce((max, el) => {
        const { position } = window.getComputedStyle(el);
        if (position !== "fixed" && position !== "sticky") return max;
        const rect = el.getBoundingClientRect();
        // Only count elements that are actually flush with the top edge right now
        return rect.top === 0 && rect.height > 0 ? Math.max(max, rect.bottom) : max;
    }, 0);
}

function scrollToItem(item) {
    const top = item.getBoundingClientRect().top + window.scrollY - getFixedHeaderHeight();
    window.scrollTo({ top, behavior: "smooth" });
}

/**
 * Show only the current page items, hide everything else in the container.
 * All items (server-rendered + WS) stay in the DOM; visibility is toggled.
 * Pass suppressScroll=true to skip scrolling (initial load, WS updates).
 */
function renderCurrentPage(container, manager, wrapper, nav, suppressScroll) {
    const pageItems = new Set(manager.getPageItems(manager.currentPage));

    // Only toggle .rebellt-item elements; all other post content stays visible
    manager.items.forEach((item) => {
        if (!container.contains(item)) {
            container.prepend(item);
        }
        item.style.display = pageItems.has(item) ? "" : "none";
    });

    updatePaginationUI(manager, nav);

    if (wrapper && manager.isOnFirstPage()) {
        wrapper.classList.remove("rebel-live-blog--has-new-data");
    }

    if (!suppressScroll) {
        const firstVisible = manager.getPageItems(manager.currentPage)[0];
        if (firstVisible) {
            scrollToItem(firstVisible);
        }
    }
}

function updatePaginationUI(manager, nav) {
    if (!nav) return;

    const totalPages = manager.getTotalPages();
    nav.style.display = totalPages <= 1 ? "none" : "";

    const info = nav.querySelector(".rebel-live-blog-pagination-info");
    info.textContent = `Page ${manager.currentPage} of ${totalPages}`;

    const prevBtn = nav.querySelector("[data-action='prev']");
    const nextBtn = nav.querySelector("[data-action='next']");

    prevBtn.disabled = manager.currentPage <= 1;
    nextBtn.disabled = manager.currentPage >= totalPages;
    prevBtn.classList.toggle(
        "rebel-live-blog-pagination-btn--disabled",
        manager.currentPage <= 1
    );
    nextBtn.classList.toggle(
        "rebel-live-blog-pagination-btn--disabled",
        manager.currentPage >= totalPages
    );
}

function mountPaginationUI(container, manager, onNavigate) {
    const nav = document.createElement("nav");
    nav.className = "rebel-live-blog-pagination-nav";
    nav.innerHTML = `
        <button class="rebel-live-blog-pagination-btn rebel-live-blog-pagination-btn--prev" data-action="prev">Previous</button>
        <span class="rebel-live-blog-pagination-info"></span>
        <button class="rebel-live-blog-pagination-btn rebel-live-blog-pagination-btn--next" data-action="next">Next</button>
    `;

    nav.addEventListener("click", (event) => {
        const action = event.target.dataset.action;
        if (!action) return;

        let targetPage = manager.currentPage;
        if (action === "prev" && targetPage > 1) targetPage--;
        if (action === "next" && targetPage < manager.getTotalPages()) targetPage++;

        if (targetPage !== manager.currentPage) {
            onNavigate(targetPage);
        }
    });

    container.parentElement.appendChild(nav);
    return nav;
}

const paginationItemHandlers = {
    [PARTICLE]: addAssemblerParticle,
    [SLIDESHOW]: addAssemblerSlideshow,
};

/**
 * Build and insert a particle element for paginated mode.
 * Notification is only triggered when the user is on page 1.
 */
function insertPaginatedParticle(container, data, settings, shouldNotify) {
    const handler = paginationItemHandlers[data.itemType];
    const particleElement = handler ? handler(data, settings) : null;
    insertParticle(container, injectAssemblerAds(settings.post.id));
    insertParticle(container, particleElement);
    if (shouldNotify) {
        particleNotify(particleElement);
    }
    return particleElement;
}

export default function postLiveBlog(context, wrapper) {
    const { settings, eventBus } = context;
    const { site, post } = settings;
    const channel = `post-particle:${site.id}:${post.id}:desktop`;

    // wrapper is the .live-updates-wrapper element (passed by loadModuleOnElement)
    const listicleBody = wrapper
        ? wrapper.querySelector(".js-listicle-body")
        : document.querySelector(".js-listicle-body");
    const container = listicleBody && listicleBody.querySelector(".body-description");

    if (!container) return;

    // Guard against double-initialisation (elementChangeListener can fire more than once
    // for the same element, which would create two navs and two WS subscriptions).
    if (container.dataset.liveblogInit) return;
    container.dataset.liveblogInit = "true";

    renderAssemblerParticleList(container, eventBus);

    const { paginated, pageSize } = readPaginationSettings(listicleBody);

    if (!paginated) {
        // Non-paginated: original behavior
        const renderParticle = makeParticleRenderer(container, context.settings);
        createMessageBusClient(settings, channel, (data) => {
            const numericEntries = Object.entries(data).reduce(numericKeysReducer, []);
            if (numericEntries.length > 0) {
                Object.values(numericEntries).forEach(renderParticle);
            } else {
                renderParticle(data);
            }
        });
        return;
    }

    // === PAGINATED MODE ===
    const resolvedWrapper = wrapper || listicleBody.closest(".live-updates-wrapper");
    if (resolvedWrapper) {
        resolvedWrapper.classList.add("rebel-live-blog--paginated");
    }

    // Collect existing server-rendered items (newest-first DOM order assumed)
    const existingItems = Array.from(container.querySelectorAll(".rebellt-item"));
    const manager = new PaginationManager({ items: existingItems, pageSize });

    const nav = mountPaginationUI(container, manager, navigateToPage);

    // Read deep-link page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialPage = parseInt(urlParams.get("liveblogpage")) || 1;

    manager.goToPage(initialPage);
    renderCurrentPage(container, manager, resolvedWrapper, nav, /* suppressScroll */ true);

    // Seed history state so popstate works on first back-navigation
    window.history.replaceState(
        { liveblogpage: manager.currentPage },
        "",
        window.location.href
    );

    // Browser back / forward
    window.addEventListener("popstate", (event) => {
        const page = (event.state && event.state.liveblogpage) || 1;
        manager.goToPage(page);
        renderCurrentPage(container, manager, resolvedWrapper, nav, /* suppressScroll */ false);
    });

    // WebSocket handler
    createMessageBusClient(settings, channel, (data) => {
        const numericEntries = Object.entries(data).reduce(numericKeysReducer, []);
        const particles =
            numericEntries.length > 0 ? Object.values(numericEntries) : [data];

        const onPage1 = manager.isOnFirstPage();

        particles.forEach((particleData) => {
            const particle = insertPaginatedParticle(
                container,
                particleData,
                context.settings,
                onPage1
            );
            if (particle) {
                updateAssemblerParticleList(particle, particleData);
                manager.addItem(particle);
            }
        });

        // Suppress built-in scroll — WS updates handle scroll explicitly below
        renderCurrentPage(container, manager, resolvedWrapper, nav, /* suppressScroll */ true);

        if (manager.isOnFirstPage()) {
            // New item is visible on page 1 — scroll to the new first particle
            const firstItem = manager.getPageItems(1)[0];
            if (firstItem) scrollToItem(firstItem);
        } else {
            // User is on page 2+ — signal that unseen items are waiting on page 1
            if (resolvedWrapper) resolvedWrapper.classList.add("rebel-live-blog--has-new-data");
        }
    });

    function navigateToPage(pageNumber) {
        manager.goToPage(pageNumber);
        renderCurrentPage(container, manager, resolvedWrapper, nav, /* suppressScroll */ false);

        const url = new URL(window.location.href);
        if (pageNumber === 1) {
            url.searchParams.delete("liveblogpage");
        } else {
            url.searchParams.set("liveblogpage", pageNumber);
        }
        window.history.pushState({ liveblogpage: pageNumber }, "", url.toString());
    }
}
